import { FormStatus } from '@dnb/eufemia';
import { HelpProps } from '@dnb/eufemia/components/help-button/HelpButtonInline';
import { Field, FieldProps, Form, useFieldProps } from '@dnb/eufemia/extensions/forms';
import { ServiceNowChangeRequestDto } from '@portals/shared/portal/SupportTicket';
import { ApiError } from '@portals/shared-frontend/utils/ApiError';
import { useCallback, useState } from 'react';

import { getChangeRequestById } from '@/api/app';
import { humanDatetime } from '@/utils';

const crqHelp: HelpProps = {
  title: 'ServiceNow Identifier (BA/BSN)',
  content:
    'A ServiceNow change request (CRQ) in the "implement" state is required for creating a client in the live environment. Enter the ID for the relevant CRQ.',
};

interface CrqFieldProps extends FieldProps {
  labelDescription?: string;
  customHelp?: HelpProps;
}

export function CrqField(props: CrqFieldProps) {
  const [crqDetails, setCrqDetails] = useState<ServiceNowChangeRequestDto | undefined>();
  const { handleChange } = useFieldProps(props);

  const validateCRQ = useCallback(async (crq: string): Promise<Error | undefined> => {
    try {
      if (!crq) {
        setCrqDetails(undefined);
        return undefined;
      }
      const fetchedDetails = await getChangeRequestById(crq);
      setCrqDetails(fetchedDetails);
      return undefined;
    } catch (error) {
      setCrqDetails(undefined);
      if (ApiError.isApiError(error)) {
        return new Error(error.body.message);
      }
      return new Error('An unexpected error occurred');
    }
  }, []);

  return (
    <>
      <Field.String
        help={props.customHelp ?? crqHelp}
        label="ServiceNow CRQ"
        labelSuffix={false}
        leftIcon={crqDetails ? 'check' : undefined}
        onBlurValidator={validateCRQ}
        onChange={handleChange}
        path="/crq"
        placeholder="CHG_DNBXXXXXXX"
        required={props.required ?? true}
        size="medium"
        width="stretch"
      />
      <Form.Visibility animate visible={crqDetails !== undefined}>
        <FormStatus
          state="information"
          text={
            crqDetails
              ? `CRQ is valid from ${humanDatetime(crqDetails.startDate)} to ${humanDatetime(crqDetails.endDate)}`
              : ''
          }
          top="small"
        />
      </Form.Visibility>
    </>
  );
}
